import {
  useGetInternationalByIdsQuery,
  useLazyGetNumberTypesQuery,
  useLazyGetRightsQuery,
  useUpdateInternationalMutation,
} from "@/react/features/globalization/globalizationApi";
import React, { useEffect, useState } from "react";
import { internationalHorizontal } from "@/react/features/globalization/columnDefinition";
import { MemoHorizontalTable } from "@/react/features/globalization/HorizontalTable";
import { useSelector } from "react-redux";
import { OpenInTapFAB } from "@/react/features/globalization/OpenInTapFAB";
import { useNavigate, useParams } from "react-router-dom";
import { canEditColumn } from "@/react/features/globalization/rights";
import { displayToastMessage } from "@/react/notifications";
import { TYPE } from "@8x8/oxygen-toaster";
import { INTERNATIONAL_FIELDS_EDIT } from "@/react/features/globalization/constants";
import styled from "styled-components";
import { CustomButton } from "@/react/features/globalization/components/CustomButton";
import { GLOBALIZATION_INTERNATIONAL } from "@/react/routing/constants";
import { ArrowLeftFullIcon } from "@8x8/oxygen-icon";
import { OperationsContainer } from "@/react/features/globalization/OperationsContainer";

const UPDATE_TEXT_KO = "Could not update";
const UPDATE_TEXT_OK = "Updated successfully";
const TOAST_DURATION = 2000;

const Container = styled.div`
  margin-left: 16px;
`;

export function GlobalizationInternationalEditPage() {
  const { id } = useParams();
  const [international, setInternational] = useState(undefined);
  const isIframe = useSelector((state) => state.application.isIframe);
  const { data: internationalList, isLoading } = useGetInternationalByIdsQuery({
    idList: [id],
  });
  const [getUserRights, userRightsResult] = useLazyGetRightsQuery();
  const { data: userRightsMap } = userRightsResult;
  const [getNumberTypes, numberTypesResult] = useLazyGetNumberTypesQuery();
  const { data: numberTypes } = numberTypesResult;

  const navigate = useNavigate();

  useEffect(() => {
    if (!isIframe) {
      getUserRights();
      getNumberTypes();
    }
  }, []);

  useEffect(() => {
    setInternational(
      internationalList && internationalList.length
        ? internationalList[0]
        : undefined
    );
  }, [internationalList]);

  const [updateInternational] = useUpdateInternationalMutation();

  const handleUpdateAPI = (property, value) => {
    const restPayload = {};
    restPayload[property] = value;
    return updateInternational({
      id: international.id,
      payload: restPayload,
    }).unwrap();
  };

  const handleOnChangeCell = (row, property, value) => {
    const newInternational = { ...international };
    newInternational[property] = value;
    setInternational(newInternational);
    handleUpdateAPI(property, value)
      .then((response) => {
        displayToastMessage(UPDATE_TEXT_OK, {
          duration: TOAST_DURATION,
          type: TYPE.info,
        });
        setInternational(response);
      })
      .catch(() =>
        displayToastMessage(UPDATE_TEXT_KO, { duration: TOAST_DURATION })
      );
  };

  return (
    <OpenInTapFAB>
      <Container>
        <OperationsContainer isStacked={isIframe}>
          <CustomButton
            onClick={() => {
              navigate(GLOBALIZATION_INTERNATIONAL);
            }}
          >
            <ArrowLeftFullIcon size={20} /> Back
          </CustomButton>
        </OperationsContainer>
        <MemoHorizontalTable
          isLoading={isLoading}
          title={"Edit"}
          data={international ? [international] : undefined}
          columns={internationalHorizontal}
          onCellChange={handleOnChangeCell}
          isEdit
          meta={{
            internationalId: parseInt(id),
            rights: userRightsMap || [],
            numberTypesOptions: (numberTypes || []).map((numberType) => {
              return { value: numberType.id, label: numberType.name };
            }),
            reload: () => {
              getInternationalByIds({ idList: [id] });
            },
          }}
          canEdit={(meta, column) =>
            canEditColumn(meta.rights || [], INTERNATIONAL_FIELDS_EDIT, column)
          }
        />
      </Container>
    </OpenInTapFAB>
  );
}
